import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { API_BASE_URL } from '../../../../api/config';
import fetchService from '../../../../services/fetchService';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('CONTACTS_COMMANDS');

export const contactsEnrichCommand: SelfContainedCommand = {
  id: 'contacts_enrich',
  description: '✨ ENRICH: Scan Gmail and Calendar to enrich a contact with interaction history, topics, signals, and next reach-out suggestion',
  autonomyLevel: 'confirm',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'contactId',
        type: 'string',
        description: 'The ID of the contact to enrich',
        required: true,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { contactId: 'contact_abc123' },
        expectedOutput: createSuccessResult('Enriched: Jane Smith', {
          contactId: 'contact_abc123',
          topics: ['platform migration', 'AI tooling'],
          signals: [],
          nextReachOutAt: '2026-03-01T00:00:00.000Z',
        }),
        description: 'Enrich a contact from Gmail and Calendar data',
      },
    ],
    bad: [],
  },

  handler: async (
    params: { contactId: string },
    context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      context.onProgress?.({
        progress: 'Scanning Gmail threads and Calendar events...',
      });

      const enriched = await fetchService(
        `${API_BASE_URL}/contacts-app/${params.contactId}/enrich`,
        { method: 'POST' }
      );

      const sk = enriched?.skeleton || {};

      context.onProgress?.({
        progress: 'Enrichment complete',
        topics: sk.topics || [],
        signals: sk.signals || [],
        lastInteraction: sk.lastInteraction,
        nextReachOutAt: sk.nextReachOutAt,
      });

      log.info('[contacts_enrich] Enriched contact', params.contactId);
      return createSuccessResult(`Enriched: ${enriched.name}`, {
        contactId: enriched._id,
        name: enriched.name,
        topics: sk.topics || [],
        signals: sk.signals || [],
        preferredChannel: sk.preferredChannel,
        lastInteraction: sk.lastInteraction,
        nextReachOutAt: sk.nextReachOutAt,
        nextReachOutReason: sk.nextReachOutReason,
        lastEnrichedAt: sk.lastEnrichedAt,
      });
    } catch (error) {
      log.error('[contacts_enrich] Failed', error);
      return createErrorResult(
        'Failed to enrich contact',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};
